import React from "react";
import ReactDOM from "react-dom";

import { FLASHCARD_SWIPE_THRESHOLD, getFlashcardSwipeResult } from "./flashcard";

export const FLASHCARD_FLY_OUT_MS = 260;
// A tapped grade starts from rest, so it flies slower to read as a swipe.
export const FLASHCARD_TAPPED_FLY_OUT_MS = 550;
export const FLASHCARD_FLIP_MS = 320;

const DRAG_START_PX = 8;
const SPRING = "450ms cubic-bezier(0.34, 1.3, 0.64, 1)";
const HAS_SEEN_DEMO_KEY = "hasSeenFlashcardSwipeDemo";
const DEMO_OFFSET_PX = 110;
const DEMO_MOVE_MS = 650;
const DEMO_HOLD_MS = 500;
const FLIP_DEGREES = 180;
const FLIP_DRAG_PX = 220;
const FLIP_SWIPE_VELOCITY = 500;
const FLIP_FINISH_MS = 200;
const FLIP_SPRING_BACK_MS = 300;

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
const easeInOutCubic = (t) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
const cardTransform = (x) => `translateX(${x}px) rotate(${x / 20}deg)`;
const clampOpacity = (value) => Math.min(Math.max(value, 0), 1);
const clampFlip = (degrees) => Math.max(-FLIP_DEGREES, Math.min(FLIP_DEGREES, degrees));

function hasSeenDemo() {
  try {
    return window.localStorage.getItem(HAS_SEEN_DEMO_KEY) === "true";
  } catch(e) {
    return true;
  }
}

function saveHasSeenDemo() {
  try {
    window.localStorage.setItem(HAS_SEEN_DEMO_KEY, "true");
  } catch(e) {
    // do nothing
  }
}

function demoKeyframes() {
  const cycle = DEMO_MOVE_MS * 4 + DEMO_HOLD_MS * 2;
  const steps = [
    [0, 0],
    [DEMO_MOVE_MS, DEMO_OFFSET_PX],
    [DEMO_MOVE_MS + DEMO_HOLD_MS, DEMO_OFFSET_PX],
    [DEMO_MOVE_MS * 2 + DEMO_HOLD_MS, 0],
    [DEMO_MOVE_MS * 3 + DEMO_HOLD_MS, -DEMO_OFFSET_PX],
    [DEMO_MOVE_MS * 3 + DEMO_HOLD_MS * 2, -DEMO_OFFSET_PX],
    [cycle, 0],
  ];
  return {
    duration: cycle,
    keyframes: steps.map(([at, x]) => ({ easing: "ease-in-out", offset: at / cycle, transform: cardTransform(x) })),
  };
}

function isFacingAway(degrees) {
  const turned = Math.abs(degrees) % 360;
  return turned > 90 && turned < 270;
}

export default class FlashcardSwipeable extends React.Component {
  constructor(props) {
    super(props);

    const { returnFrom } = props;
    this.flipDegrees = 0;
    this.state = {
      dragX: returnFrom ? (returnFrom === "right" ? 1 : -1) * window.innerWidth : 0,
      isDragging: false,
      isReturning: !!returnFrom,
    };
  }

  componentDidMount() {
    if(this.state.isReturning) {
      window.requestAnimationFrame(() => window.requestAnimationFrame(() => this.setState({ dragX: 0, isReturning: false })));
    }
  }

  componentDidUpdate(prevProps) {
    if(this.props.isRevealed && !prevProps.isRevealed && !hasSeenDemo()) {
      saveHasSeenDemo();
      this.props.onDemoShown();
      this.runDemo();
    }
    if(this.props.flyOut && !prevProps.flyOut) {
      this.cancelDemo();
      this.flyOutTimeout = setTimeout(() => this.props.onFlyOutDone(), this.getFlyOutMs());
    }
  }

  componentWillUnmount() {
    this.cancelDemo();
    this.cancelFlipTween();
    clearTimeout(this.flyOutTimeout);
  }

  runDemo() {
    if(!this.cardEl || !this.cardEl.animate) {
      return;
    }
    const { duration, keyframes } = demoKeyframes();
    this.demoAnimation = this.cardEl.animate(keyframes, { duration, iterations: 2 });
  }

  cancelDemo() {
    if(this.demoAnimation) {
      this.demoAnimation.cancel();
      this.demoAnimation = null;
    }
  }

  startFlip(direction = 1) {
    if(!this.props.canFlip || this.isFlipping) {
      return;
    }
    this.isFlipping = true;
    const target = direction * FLIP_DEGREES;
    const duration = this.flipDegrees === 0 ? FLASHCARD_FLIP_MS : FLIP_FINISH_MS;
    const easing = this.flipDegrees === 0 ? easeInOutCubic : easeOutCubic;
    this.tweenFlip(target, duration, easing, () => this.finishFlip());
  }

  // The back face already shows the answer, so the real reveal and the reset to the front land in one commit.
  finishFlip() {
    ReactDOM.unstable_batchedUpdates(() => {
      this.props.onFlipped();
      this.isFlipping = false;
      this.setFlipDegrees(0);
    });
  }

  tweenFlip(target, duration, easing, onDone) {
    this.cancelFlipTween();
    const from = this.flipDegrees;
    const start = window.performance.now();
    const step = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      this.setFlipDegrees(from + (target - from) * easing(progress));
      if(progress < 1) {
        this.flipFrame = window.requestAnimationFrame(step);
      }
      else {
        this.flipFrame = null;
        onDone && onDone();
      }
    };
    this.flipFrame = window.requestAnimationFrame(step);
  }

  cancelFlipTween() {
    if(this.flipFrame) {
      window.cancelAnimationFrame(this.flipFrame);
      this.flipFrame = null;
    }
  }

  setFlipDegrees(degrees) {
    this.flipDegrees = degrees;
    this.applyFlipStyles();
  }

  applyFlipStyles() {
    const degrees = this.flipDegrees;
    const turn = Math.sin(Math.abs(degrees) * Math.PI / FLIP_DEGREES);
    if(this.turnEl) {
      this.turnEl.style.transform = `scale(${1 - 0.14 * turn})`;
    }
    // backface-visibility alone lets a face that scrolls its own content show through mirrored
    if(this.frontEl) {
      this.frontEl.style.transform = `perspective(1400px) rotateY(${degrees}deg)`;
      this.frontEl.style.visibility = isFacingAway(degrees) ? "hidden" : "";
    }
    if(this.backEl) {
      this.backEl.style.transform = `perspective(1400px) rotateY(${degrees + FLIP_DEGREES}deg)`;
      this.backEl.style.visibility = isFacingAway(degrees + FLIP_DEGREES) ? "hidden" : "";
    }
    // The next card waits behind for the fly-out; during a turn it must not show through the edge.
    if(this.previewEl) {
      this.previewEl.style.opacity = degrees === 0 ? 1 : 0;
    }
  }

  getFlyOutMs() {
    return Math.abs(this.state.dragX) > DRAG_START_PX ? FLASHCARD_FLY_OUT_MS : FLASHCARD_TAPPED_FLY_OUT_MS;
  }

  isFlipDrag() {
    return this.props.canFlip && !this.isFlipping;
  }

  onPointerDown(e) {
    if((this.pointer && this.pointer.isDragging) || (!this.props.canGrade && !this.isFlipDrag())) {
      return;
    }
    if(this.demoAnimation) {
      this.cancelDemo();
      this.setState({ dragX: 0 });
    }
    this.pointer = { id: e.pointerId, lastTime: Date.now(), lastX: e.clientX, startX: e.clientX, velocityX: 0 };
  }

  isTrackedPointer(e) {
    return !!this.pointer && this.pointer.id === e.pointerId;
  }

  onPointerMove(e) {
    if(!this.isTrackedPointer(e)) {
      return;
    }

    const dragX = e.clientX - this.pointer.startX;
    if(!this.pointer.isDragging && Math.abs(dragX) < DRAG_START_PX) {
      return;
    }

    const now = Date.now();
    const elapsed = now - this.pointer.lastTime;
    if(elapsed > 0) {
      this.pointer.velocityX = (e.clientX - this.pointer.lastX) / elapsed * 1000;
    }
    this.pointer.lastTime = now;
    this.pointer.lastX = e.clientX;

    if(!this.pointer.isDragging && e.currentTarget.setPointerCapture && e.pointerId !== undefined) {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    this.pointer.isDragging = true;
    // A mouse drag would otherwise select the sentence and open the translation popover on release.
    window.getSelection && window.getSelection().removeAllRanges();

    if(!this.props.canGrade) {
      if(this.isFlipDrag()) {
        this.cancelFlipTween();
        this.setFlipDegrees(clampFlip(dragX / FLIP_DRAG_PX * FLIP_DEGREES));
      }
      return;
    }
    this.setState({ dragX, isDragging: true });
  }

  onPointerUp(e) {
    if(!this.isTrackedPointer(e)) {
      return;
    }
    const { pointer } = this;
    this.pointer = null;

    if(!pointer || !pointer.isDragging) {
      return;
    }

    this.wasDragged = true;
    if(!this.props.canGrade) {
      this.releaseFlipDrag(pointer);
      return;
    }

    const result = getFlashcardSwipeResult({ translationX: this.state.dragX, velocityX: pointer.velocityX });
    this.setState({ dragX: result ? this.state.dragX : 0, isDragging: false });
    if(result) {
      this.props.onSwipe(result);
    }
  }

  releaseFlipDrag({ lastX, startX, velocityX }) {
    if(!this.isFlipDrag()) {
      return;
    }
    const translationX = lastX - startX;
    const isPastHalfway = Math.abs(translationX) > FLIP_DRAG_PX / 2;
    const isFlicked = Math.abs(velocityX) > FLIP_SWIPE_VELOCITY;
    if(!isPastHalfway && !isFlicked) {
      this.tweenFlip(0, FLIP_SPRING_BACK_MS, easeOutCubic);
      return;
    }
    this.startFlip((translationX || velocityX) < 0 ? -1 : 1);
  }

  onPointerCancel(e) {
    if(!this.isTrackedPointer(e)) {
      return;
    }
    this.pointer = null;
    if(this.isFlipDrag()) {
      this.tweenFlip(0, FLIP_SPRING_BACK_MS, easeOutCubic);
    }
    this.setState({ dragX: 0, isDragging: false });
  }

  onClickCapture(e) {
    if(this.wasDragged) {
      e.stopPropagation();
      this.wasDragged = false;
    }
  }

  getCardTransform() {
    const { flyOut } = this.props;
    if(flyOut) {
      return cardTransform((flyOut === "right" ? 1 : -1) * window.innerWidth * 1.3);
    }
    return cardTransform(this.state.dragX);
  }

  getTransition() {
    const { flyOut } = this.props;
    if(flyOut) {
      return Math.abs(this.state.dragX) > DRAG_START_PX
        ? `transform ${FLASHCARD_FLY_OUT_MS}ms cubic-bezier(0.33, 1, 0.68, 1)`
        : `transform ${FLASHCARD_TAPPED_FLY_OUT_MS}ms cubic-bezier(0.65, 0, 0.35, 1)`;
    }
    return this.state.isDragging || this.state.isReturning ? "none" : `transform ${SPRING}`;
  }

  getSwipeOpacity(direction) {
    const { flyOut } = this.props;
    if(flyOut) {
      return flyOut === direction ? 1 : 0;
    }
    const { dragX } = this.state;
    return clampOpacity((direction === "right" ? dragX : -dragX) / FLASHCARD_SWIPE_THRESHOLD);
  }

  render() {
    const { backFace, children, isRevealed, preview } = this.props;

    return (
      <div className="flashcard-stack">
        {preview && (
          <div aria-hidden="true" className="flashcard-preview-slot" inert="" ref={(el) => this.previewEl = el}>{preview}</div>
        )}
        <div
          className="flashcard-swipeable"
          onClickCapture={(e) => this.onClickCapture(e)}
          onPointerCancel={(e) => this.onPointerCancel(e)}
          onPointerDown={(e) => this.onPointerDown(e)}
          onPointerMove={(e) => this.onPointerMove(e)}
          onPointerUp={(e) => this.onPointerUp(e)}
          ref={(el) => this.cardEl = el}
          style={{ transform: this.getCardTransform(), transition: this.getTransition() }}
        >
          <div className="flashcard-turn" ref={(el) => this.turnEl = el}>
            <div className="flashcard-face" ref={(el) => this.frontEl = el}>{children}</div>
            {!isRevealed && (
              <div aria-hidden="true" className="flashcard-face flashcard-back-face-slot" inert="" ref={(el) => { this.backEl = el; this.applyFlipStyles(); }}>
                {backFace}
              </div>
            )}
          </div>
          {isRevealed && (
            <>
              <div className="flashcard-swipe-edge flashcard-swipe-edge-good" style={{ opacity: this.getSwipeOpacity("right") }} />
              <div className="flashcard-swipe-edge flashcard-swipe-edge-again" style={{ opacity: this.getSwipeOpacity("left") }} />
              <div className="flashcard-stamp flashcard-stamp-good joystix" style={{ opacity: this.getSwipeOpacity("right") }}>Good</div>
              <div className="flashcard-stamp flashcard-stamp-again joystix" style={{ opacity: this.getSwipeOpacity("left") }}>Again</div>
            </>
          )}
        </div>
      </div>
    );
  }
}
